import { useQuery } from '@tanstack/react-query';
import { api, apiUrl, upload } from './api';
import { resizeImage } from './images';

/** Screenshots and photos a technology ticket can have. */
export const MAX_TICKET_IMAGES = 10;

export interface TicketImage {
  id: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  uploadedByName: string;
  createdAt: string;
  /** Technicians remove any picture; others the ones they added. */
  canDelete: boolean;
}

export const ticketImageUrl = (workOrderId: string, imageId: string) => apiUrl(`/work-orders/${workOrderId}/images/${imageId}`);

const EXTENSION: Record<string, string> = { 'image/webp': 'webp', 'image/jpeg': 'jpg', 'image/png': 'png' };

/**
 * Resizes a screenshot or photo (at most 2,400 pixels across and 4 MB, which also drops where a photo was taken) and
 * uploads it to a ticket, or on its own for a ticket that isn't sent yet.
 */
export async function uploadTicketImage(file: File, workOrderId?: string) {
  const { blob } = await resizeImage(file, { maxSide: 2400, maxBytes: 4 * 1024 * 1024, quality: 0.9 });
  const base = file.name.replace(/\.[^.]+$/, '').trim() || 'picture';
  const fileName = `${base}.${EXTENSION[blob.type] ?? 'jpg'}`.slice(0, 255);
  return upload<TicketImage>(workOrderId ? `/work-orders/${workOrderId}/images` : '/work-orders/images', blob, {
    'x-picture': encodeURIComponent(JSON.stringify({ fileName })),
  });
}

/** Optional fields on the "Report a problem" form that an administrator can hide from requesters. */
export type RequestFormField = 'DEPARTMENT' | 'PRIORITY' | 'CATEGORY';

export const REQUEST_FORM_FIELDS: { value: RequestFormField; label: string; hidden: string }[] = [
  { value: 'DEPARTMENT', label: 'Which team should handle it?', hidden: 'Tickets start without a department, so workflows route them.' },
  { value: 'PRIORITY', label: 'How urgent is it?', hidden: 'Tickets start at Normal priority.' },
  { value: 'CATEGORY', label: 'Type of problem', hidden: 'Tickets start as “Other”, or “Apps or software” when they’re about software.' },
];

/** The fields hidden from requesters on the technology help desk's "Report a problem" form. */
export const useRequestForm = (enabled = true) =>
  useQuery({
    queryKey: ['request-form'],
    queryFn: () => api.get<{ hiddenFields: RequestFormField[] }>('/helpdesk/request-form'),
    enabled,
    staleTime: 60_000,
  });
